import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Post {
  id: string;
  title: string;
  date: string;
  content: string;
}

interface IndexViewProps {
  posts: Post[];
  blogSlug: string;
  isOwner?: boolean;
}

export function IndexView({ posts, blogSlug, isOwner = false }: IndexViewProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 20;
  const totalPages = Math.ceil(posts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const visiblePosts = posts.slice(startIndex, startIndex + postsPerPage);

  // Handle empty posts array
  if (!posts.length) {
    return (
      <div className="text-center py-12 text-gray-500">
        {isOwner ? "Write your first post to get started!" : "No posts available."}
      </div>
    );
  }

  // Group visible posts by year
  const groupedPosts = visiblePosts.reduce((acc, post) => {
    if (!post) return acc; // Skip undefined posts
    
    const date = new Date(post.date);
    const year = date.getFullYear();
    
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(post);
    return acc;
  }, {} as Record<number, Post[]>);

  // Sort years in descending order
  const sortedYears = Object.keys(groupedPosts)
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <div>
      <div className="space-y-12">
        {sortedYears.map(year => (
          <div key={year}>
            <h2 className="text-2xl font-bold mb-6">{year}</h2>
            <div className="divide-y divide-gray-200">
              {groupedPosts[year].map(post => (
                <Link
                  key={post.id}
                  to={`/${blogSlug}/${post.id}`}
                  className="block py-3 group"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <span className="text-[1.2rem] font-medium leading-tight relative group-hover:bg-yellow-100 transition-colors px-1 -mx-1">
                      {post.title}
                    </span>
                    <time 
                      className="hidden sm:block text-sm text-gray-400 sm:ml-4 shrink-0"
                      dateTime={new Date(post.date).toISOString()}
                    >
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: '2-digit'
                      })}
                    </time>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      {posts.length > postsPerPage && totalPages > 1 && (
        <nav
          className="flex justify-center items-center gap-1 mt-8 pb-8"
          aria-label="Pagination"
        >
          {/* Previous Button */}
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>

          {/* Page Numbers */}
          {(() => {
            const pages: (number | string)[] = [];
            const showEllipsisStart = currentPage > 3;
            const showEllipsisEnd = currentPage < totalPages - 2;

            // Always show first page
            pages.push(1);

            // Add ellipsis after first page if needed
            if (showEllipsisStart) {
              pages.push('ellipsis-start');
            }

            // Add pages around current page
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
              if (i !== 1 && i !== totalPages) {
                pages.push(i);
              }
            }

            // Add ellipsis before last page if needed
            if (showEllipsisEnd) {
              pages.push('ellipsis-end');
            }

            // Always show last page if more than 1 page
            if (totalPages > 1) {
              pages.push(totalPages);
            }

            return pages.map((page, index) => {
              if (typeof page === 'string') {
                return (
                  <span key={page} className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors ${
                    currentPage === page
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100 border border-gray-300'
                  }`}
                  aria-label={`Go to page ${page}`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              );
            });
          })()}

          {/* Next Button */}
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </nav>
      )}
    </div>
  );
}